import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, IconButton, Button, useDisclosure, useToast, Text,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Switch, VStack, Textarea,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import cancellationPolicyService from '../services/cancellationPolicyService';

const emptyForm = { name: '', description: '', hours_before_checkin: 24, penalty_percentage: '0.00', is_active: true };

const CancellationPolicies = () => {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await cancellationPolicyService.getAll();
      const list = Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : []);
      setPolicies(list);
    } catch { setError(t('cancellationPolicies.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); onOpen(); };
  const openEdit = (p) => {
    setForm({
      name: p.name || '',
      description: p.description || '',
      hours_before_checkin: p.hours_before_checkin || 24,
      penalty_percentage: p.penalty_percentage || '0.00',
      is_active: p.is_active ?? true,
    });
    setEditingId(p.id);
    onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) await cancellationPolicyService.update(editingId, form);
      else {
        const hotelId = localStorage.getItem('admin_hotel_id');
        await cancellationPolicyService.create({ ...form, hotel: hotelId ? Number(hotelId) : undefined });
      }
      toast({ title: editingId ? t('cancellationPolicies.toasts.updated') : t('cancellationPolicies.toasts.created'), status: 'success', duration: 2000 });
      onClose(); load();
    } catch (err) { toast({ title: t('cancellationPolicies.toasts.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await cancellationPolicyService.remove(deleteTarget.id); toast({ title: t('cancellationPolicies.toasts.deleted'), status: 'success', duration: 2000 }); deleteDisclosure.onClose(); load(); }
    catch { toast({ title: t('cancellationPolicies.toasts.failed'), status: 'error', duration: 3000 }); }
  };

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const formatHours = (hours) => {
    if (!hours) return '-';
    if (hours >= 24) return t('cancellationPolicies.window.daysBefore', { count: Math.floor(hours / 24) });
    return t('cancellationPolicies.window.hoursBefore', { count: hours });
  };

  return (
    <PageShell title={t('cancellationPolicies.title')} subtitle={t('cancellationPolicies.count', { count: policies.length })}
      breadcrumbs={[{ label: t('cancellationPolicies.title') }]} loading={loading} error={error}
      actions={<Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>{t('cancellationPolicies.buttons.add')}</Button>}>

      <Card shadow="sm" borderWidth="1px"><CardBody p={0}><TableContainer><Table size="sm">
        <Thead><Tr><Th>{t('cancellationPolicies.fields.name')}</Th><Th>{t('cancellationPolicies.columns.window')}</Th><Th>{t('cancellationPolicies.columns.penalty')}</Th><Th>{t('cancellationPolicies.columns.status')}</Th><Th>{t('cancellationPolicies.columns.actions')}</Th></Tr></Thead>
        <Tbody>
          {policies.map(p => (
            <Tr key={p.id}>
              <Td><VStack align="start" spacing={0}><Text fontSize="sm" fontWeight="500">{p.name}</Text><Text fontSize="xs" color="gray.400">{p.description}</Text></VStack></Td>
              <Td fontSize="sm">{formatHours(p.hours_before_checkin)}</Td>
              <Td fontSize="sm">{p.penalty_percentage}%</Td>
              <Td><Badge colorScheme={p.is_active ? 'green' : 'gray'} fontSize="xs">{p.is_active ? t('cancellationPolicies.status.active') : t('cancellationPolicies.status.inactive')}</Badge></Td>
              <Td><HStack spacing={1}>
                <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(p)} aria-label={t('cancellationPolicies.buttons.edit')} />
                <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red" onClick={() => { setDeleteTarget(p); deleteDisclosure.onOpen(); }} aria-label={t('cancellationPolicies.buttons.delete')} />
              </HStack></Td>
            </Tr>
          ))}
          {policies.length === 0 && <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.400">{t('cancellationPolicies.emptyState')}</Td></Tr>}
        </Tbody>
      </Table></TableContainer></CardBody></Card>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay /><ModalContent>
          <ModalHeader>{editingId ? t('cancellationPolicies.modal.editTitle') : t('cancellationPolicies.buttons.add')}</ModalHeader><ModalCloseButton />
          <ModalBody><VStack spacing={4}>
            <FormControl isRequired><FormLabel fontSize="sm">{t('cancellationPolicies.fields.name')}</FormLabel><Input size="sm" value={form.name} onChange={set('name')} placeholder={t('cancellationPolicies.fields.namePlaceholder')} /></FormControl>
            <FormControl isRequired><FormLabel fontSize="sm">{t('cancellationPolicies.fields.description')}</FormLabel><Textarea size="sm" value={form.description} onChange={set('description')} rows={2} placeholder={t('cancellationPolicies.fields.descriptionPlaceholder')} /></FormControl>
            <HStack w="100%" spacing={4}>
              <FormControl><FormLabel fontSize="sm">{t('cancellationPolicies.fields.hoursBeforeCheckin')}</FormLabel><Input size="sm" type="number" value={form.hours_before_checkin} onChange={set('hours_before_checkin')} /></FormControl>
              <FormControl><FormLabel fontSize="sm">{t('cancellationPolicies.fields.penalty')}</FormLabel><Input size="sm" type="number" value={form.penalty_percentage} onChange={set('penalty_percentage')} /></FormControl>
            </HStack>
            <FormControl display="flex" alignItems="center"><FormLabel fontSize="sm" mb={0}>{t('cancellationPolicies.status.active')}</FormLabel>
              <Switch isChecked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /></FormControl>
          </VStack></ModalBody>
          <ModalFooter><Button variant="ghost" mr={3} onClick={onClose}>{t('cancellationPolicies.buttons.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>{editingId ? t('cancellationPolicies.buttons.save') : t('cancellationPolicies.buttons.create')}</Button></ModalFooter>
        </ModalContent></Modal>

      <ConfirmModal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete} title={t('cancellationPolicies.deleteModal.title')} message={t('cancellationPolicies.deleteModal.message', { name: deleteTarget?.name })} />
    </PageShell>
  );
};

export default CancellationPolicies;
